import React, { forwardRef, HTMLInputTypeAttribute, useCallback, useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { colorSystem, fontSystem } from '@/styles/theme'
import { Icon, IconProps, VStack } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'
import FieldLabel from '@/components/content/FieldLabel'

export interface InputProps extends SizeProps {
  inputId?: string
  inputClassName?: string
  className?: string
  placeholder?: string
  block?: boolean
  type?: HTMLInputTypeAttribute
  value?: string | number
  maxLength?: number
  error?: string
  autoFocus?: boolean
  disabled?: boolean
  readOnly?: boolean
  label?: string
  labelWidth?: number
  labelHeight?: number
  unitLabel?: string
  required?: boolean
  rightIcon?: IconProps
  helpText?: string
  onChange?: (v: string) => void
  onClick?: () => void
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onEnter?: (v: string, e: React.KeyboardEvent<HTMLInputElement>) => void
}

const DEFAULT_INPUT_HEIGHT = 28

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    inputId,
    inputClassName,
    className,
    placeholder = '내용을 입력해 주세요.',
    block = false,
    type = 'text',
    value,
    maxLength,
    autoFocus,
    error,
    disabled = false,
    readOnly = false,
    label,
    labelWidth = 88,
    labelHeight,
    unitLabel,
    required = false,
    rightIcon,
    helpText,
    onChange,
    onKeyUp,
    onKeyDown,
    onFocus,
    onBlur,
    onEnter,
    onClick,
    size = 'md',
  } = props

  const [inputHeight, setInputHeight] = useState(DEFAULT_INPUT_HEIGHT)

  // ref가 설정된 후 높이 측정
  useEffect(() => {
    const currentRef = typeof ref === 'function' ? null : ref?.current
    if (currentRef) {
      const height = currentRef.offsetHeight
      setInputHeight(height)
    }
  }, [ref])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!onChange) return

      const value = e.currentTarget.value
      if (type === 'number') {
        onChange(value === '' ? '' : value?.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))
      } else {
        onChange(value)
      }
    },
    [type, onChange],
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) onKeyDown(e)

    if (e.key === 'Enter' && e.keyCode === 13 && onEnter) onEnter(value?.toString() ?? '', e)
  }

  const handleCopyAndPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (type === 'password') e.preventDefault()
    },
    [type],
  )

  return (
    <InputWrapper className={className} $block={block} $error={!!error || !!helpText}>
      {label && <FieldLabel className="input-label" width={labelWidth} height={labelHeight} required={required} content={label} />}
      <VStack block gap={4}>
        <InnerWrapper $label={!!label} $labelWidth={labelWidth}>
          <StyledInput
            id={inputId}
            className={inputClassName}
            ref={ref}
            $pr={!!unitLabel || !!rightIcon}
            type={type === 'number' ? 'tel' : type || 'text'}
            placeholder={placeholder}
            value={value ?? ''}
            required={required}
            disabled={disabled}
            autoFocus={autoFocus}
            readOnly={readOnly}
            onChange={handleChange}
            onKeyUp={onKeyUp}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            onFocus={onFocus}
            onClick={onClick}
            $size={size}
            maxLength={maxLength}
            onCopy={handleCopyAndPaste}
            onPaste={handleCopyAndPaste}
            onCut={handleCopyAndPaste}
          />
          {unitLabel && (
            <UnitLabel $hasError={!!error} $inputHeight={inputHeight}>
              {unitLabel}
            </UnitLabel>
          )}
          {rightIcon && (
            <InputIcon className="input-icon" size={16} color="gray500" $hasError={!!error} $inputHeight={inputHeight} {...rightIcon} />
          )}
          {error && <ErrorMessage className="input-error-message">{error}</ErrorMessage>}
        </InnerWrapper>
        {helpText && (
          <Text kind="Body_12_regular" color={colorSystem.text.common.secondary}>
            {helpText}
          </Text>
        )}
      </VStack>
    </InputWrapper>
  )
})

const InputWrapper = styled.div<Record<'$error' | '$block', boolean>>`
  display: flex;
  align-items: center;

  ${({ $error }) =>
    $error &&
    css`
      align-items: start;
    `}

  ${({ $block }) =>
    $block &&
    css`
      width: 100% !important;
    `}
`

const StyledInput = styled.input<{ $pr?: boolean; $size: Size }>`
  width: 100%;
  height: ${({ $size }) => {
    switch ($size) {
      case 'sm':
        return '24px'
      case 'md':
        return '28px'
      case 'lg':
        return '36px'
      default:
        return '28px'
    }
  }};
  padding: 3px 8px 0;
  border: 1px solid ${colorSystem.border.element};
  border-radius: 4px;
  color: ${colorSystem.text.common.primary};
  ${fontSystem.Body_14_regular};

  ${({ $pr }) =>
    $pr &&
    css`
      padding-right: 28px;
    `}
  &::placeholder {
    color: ${colorSystem.text.common.tertiary};
  }

  &:focus {
    border: 1px solid ${colorSystem.border.element_active};
    color: ${colorSystem.text.common.primary};
  }

  &:read-only {
    cursor: default;
    background-color: ${colorSystem.bg.input.disabled};
  }

  &:disabled {
    background-color: ${colorSystem.bg.input.disabled};
  }
`

const InnerWrapper = styled.div<{ $label?: boolean; $labelWidth: number }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${({ $label, $labelWidth }) => ($label ? `calc(100% - ${$labelWidth}px)` : '100%')};
  min-height: ${DEFAULT_INPUT_HEIGHT}px;
`

const UnitLabel = styled.div<{ $hasError: boolean; $inputHeight: number }>`
  position: absolute;
  right: 8px;
  top: ${({ $hasError, $inputHeight }) => ($hasError ? $inputHeight / 2 + 'px' : '50%')};
  transform: translateY(-50%);
  z-index: 1;
  pointer-events: none;
`

const InputIcon = styled(Icon)<{ $hasError: boolean; $inputHeight: number }>`
  position: absolute;
  right: 8px;
  top: ${({ $hasError, $inputHeight }) => ($hasError ? $inputHeight / 2 + 'px' : '50%')};
  transform: translateY(calc(-50%));
  z-index: 1;
`

const ErrorMessage = styled.div`
  color: ${colorSystem.text.common.error};
  font-size: 12px;
  line-height: 18px;
  padding-left: 8px;
  margin-top: 2px;
  text-align: left;
`

export default Input
